/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { isBitSet, getAlarmLedColor } from "../utils";
import {
  buildPowerDetectorParams,
  createPowerDetectorChartConfig,
  PowerDetectorParams,
} from "./dasChartUtils";

const Dxru10gDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  const normalizeAttrKey = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const getAttrValueInsensitive = (key: string) => {
    const direct = parsedAttributes[key];
    if (direct !== undefined) return direct;

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(parsedAttributes).find((k) => k.toLowerCase() === lowerKey);
    if (matchedKey) return parsedAttributes[matchedKey];

    const normalizedKey = normalizeAttrKey(key);
    const matchedNormalizedKey = Object.keys(parsedAttributes).find(
      (k) => normalizeAttrKey(k) === normalizedKey
    );
    return matchedNormalizedKey ? parsedAttributes[matchedNormalizedKey] : undefined;
  };

  const getDxru10gValue = (suffix: string) => {
    const preferred = getAttrValueInsensitive(`dxRU10G${suffix}`);
    if (preferred !== undefined) return preferred;

    const altCasing = getAttrValueInsensitive(`dxRU10g${suffix}`);
    if (altCasing !== undefined) return altCasing;

    // Defensive fallback in case backend sends legacy keys
    return getAttrValueInsensitive(`dxRU${suffix}`);
  };

  const getDxru10gPortValue = (suffix: string, idx: number) => getDxru10gValue(`${suffix}${idx}`);
  const getDxru10gSfpValue = (suffix: string, idx: number) => getDxru10gValue(`${suffix}-${idx}`);

  const dxru10gAlarms = getDxru10gValue("Alarms");
  const dxru10gAlarmsMask = getDxru10gValue("AlarmsMask");
  const hasDxru10gAlarms = dxru10gAlarms !== undefined || dxru10gAlarmsMask !== undefined;

  const dxru10gConnectDeuPortNum = getDxru10gValue("ConnectDeuPortNum");
  const dxru10gEquipmentModel = getDxru10gValue("EquipmentModel") || device.model;
  const dxru10gEquipmentSn = getDxru10gValue("EquipmentSN") || device.serialNumber;
  const dxru10gSiteInfo = getDxru10gValue("SiteInfo");
  const dxru10gTemperature = getDxru10gValue("Temperature");
  const dxru10gFirmwareVersion = getDxru10gValue("Firmwareversion");
  const dxru10gHardwareVersion = getDxru10gValue("Hardwareversion");
  const hasDeviceInfo =
    dxru10gConnectDeuPortNum ||
    dxru10gEquipmentModel ||
    dxru10gEquipmentSn ||
    dxru10gSiteInfo ||
    dxru10gTemperature !== undefined ||
    dxru10gFirmwareVersion ||
    dxru10gHardwareVersion;

  const getArrayAttr = (keys: string[]) => {
    for (const key of keys) {
      const value = getAttrValueInsensitive(key);
      if (Array.isArray(value)) {
        return value;
      }
    }

    return [] as any[];
  };

  const dxru10gFanAlarm = getArrayAttr(["dxRU10GFanAlram", "dxRU10GFanAlarm"]);
  const dxru10gVswrAlarm = getArrayAttr(["dxRU10GVswrAlram", "dxRU10GVswrAlarm"]);
  const dxru10gAntennaAlarm = getArrayAttr(["dxRU10GAntennaAlram", "dxRU10GAntennaAlarm"]);
  const dxru10gPaAlarm = getArrayAttr(["dxRU10GPaAlram", "dxRU10GPaAlarm"]);

  const sortByNumber = (items: any[], key: string) =>
    [...items].sort((a, b) => Number(a?.[key]) - Number(b?.[key]));

  const dxru10gFanAlarmSorted = sortByNumber(dxru10gFanAlarm, "dxRu10GFanNumber");
  const dxru10gVswrAlarmSorted = sortByNumber(dxru10gVswrAlarm, "dxRu10GVswrNumber");
  const dxru10gAntennaAlarmSorted = sortByNumber(dxru10gAntennaAlarm, "dxRu10GAntennaNumber");
  const dxru10gPaAlarmSorted = sortByNumber(dxru10gPaAlarm, "dxRu10GPaNumber");

  const hasFanAlarm = dxru10gFanAlarmSorted.length > 0;
  const hasVswrAlarm = dxru10gVswrAlarmSorted.length > 0;
  const hasAntennaAlarm = dxru10gAntennaAlarmSorted.length > 0;
  const hasPaAlarm = dxru10gPaAlarmSorted.length > 0;

  const remoteEquipmentObjectKey = useMemo(() => {
    return (
      device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      "DXRU10G-1"
    );
  }, [
    device.objectKey,
    parsedAttributes.objectKey,
    parsedAttributes.manageObject,
    parsedAttributes.managedObject,
    parsedAttributes.objectId,
  ]);

  const serviceDeviceObjectKey =
    siteObjectKey || device.servicesDeviceObjectKey || device.serialNumber || "";

  const dxru10gSwitchColumns = [1, 2, 3, 4, 5, 6, 7, 8];
  const hasDxru10gRfInfo = dxru10gSwitchColumns.some((idx) => (
    getDxru10gPortValue("RFSwitch", idx) !== undefined ||
    getDxru10gPortValue("DlPwrOut", idx) !== undefined ||
    getDxru10gPortValue("UlPwrIn", idx) !== undefined
  ));

  const dxru10gSfpColumns = [1, 2, 3];
  const hasDxru10gSfpInfo = dxru10gSfpColumns.some((idx) => (
    getDxru10gSfpValue("SFPAlarms", idx) !== undefined ||
    getDxru10gSfpValue("SFPAlarmsMask", idx) !== undefined
  ));

  const dlPowerDetectorIndicators = dxru10gSwitchColumns.map((idx) => `dxRU10GDlPwrOut${idx}`);
  const ulPowerDetectorIndicators = dxru10gSwitchColumns.map((idx) => `dxRU10GUlPwrIn${idx}`);

  const hasDlPowerDetectorChart = dxru10gSwitchColumns.some(
    (idx) => getDxru10gPortValue("DlPwrOut", idx) !== undefined
  );

  const hasUlPowerDetectorChart = dxru10gSwitchColumns.some(
    (idx) => getDxru10gPortValue("UlPwrIn", idx) !== undefined
  );

  const hasPowerDetectorChart = hasDlPowerDetectorChart || hasUlPowerDetectorChart;

  const dlPowerDetectorBandNames = [
    "DL Port 1",
    "DL Port 2",
    "DL Port 3",
    "DL Port 4",
    "DL Port 5",
    "DL Port 6",
    "DL Port 7",
    "DL Port 8",
  ];

  const ulPowerDetectorBandNames = [
    "UL Port 1",
    "UL Port 2",
    "UL Port 3",
    "UL Port 4",
    "UL Port 5",
    "UL Port 6",
    "UL Port 7",
    "UL Port 8",
  ];

  const powerDetectorBandNumbers = [1, 2, 3, 4, 5, 6, 7, 8];

  const dlPowerDetectorChartParams = useMemo<PowerDetectorParams | null>(() => {
    if (!hasDlPowerDetectorChart) return null;

    return {
      remoteEquipmentObjectKey,
      indicators: dlPowerDetectorIndicators,
      bandNames: dlPowerDetectorBandNames,
      bandNumbers: powerDetectorBandNumbers,
      yAxisMin: -600,
      yAxisMax: 100,
    };
  }, [hasDlPowerDetectorChart, remoteEquipmentObjectKey]);

  const ulPowerDetectorChartParams = useMemo<PowerDetectorParams | null>(() => {
    if (!hasUlPowerDetectorChart) return null;

    return {
      remoteEquipmentObjectKey,
      indicators: ulPowerDetectorIndicators,
      bandNames: ulPowerDetectorBandNames,
      bandNumbers: powerDetectorBandNumbers,
      yAxisMin: -600,
      yAxisMax: 100,
    };
  }, [hasUlPowerDetectorChart, remoteEquipmentObjectKey]);

  const dlPowerDetectorChartConfig = useMemo(
    () =>
      dlPowerDetectorChartParams
        ? createPowerDetectorChartConfig("dl", dlPowerDetectorChartParams)
        : null,
    [dlPowerDetectorChartParams]
  );

  const ulPowerDetectorChartConfig = useMemo(
    () =>
      ulPowerDetectorChartParams
        ? createPowerDetectorChartConfig("ul", ulPowerDetectorChartParams)
        : null,
    [ulPowerDetectorChartParams]
  );

  const dlPowerDetectorAdditionalParams = useMemo(
    () =>
      dlPowerDetectorChartParams
        ? {
            ...buildPowerDetectorParams(
              serviceDeviceObjectKey,
              remoteEquipmentObjectKey,
              dlPowerDetectorIndicators
            ),
          }
        : null,
    [dlPowerDetectorChartParams, serviceDeviceObjectKey, remoteEquipmentObjectKey]
  );

  const ulPowerDetectorAdditionalParams = useMemo(
    () =>
      ulPowerDetectorChartParams
        ? {
            ...buildPowerDetectorParams(
              serviceDeviceObjectKey,
              remoteEquipmentObjectKey,
              ulPowerDetectorIndicators
            ),
          }
        : null,
    [ulPowerDetectorChartParams, serviceDeviceObjectKey, remoteEquipmentObjectKey]
  );

  const [fullscreenChartType, setFullscreenChartType] = useState<"dl" | "ul" | null>(null);

  const dxru10gSfpAlarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Cpri Link Rx Alarm" },
    { bit: 2, name: "Cpri Link Tx Alarm" },
    { bit: 3, name: "Cpri Sync Alarm" },
    { bit: 4, name: "Cpri Temperature Alarm" },
    { bit: 5, name: "Cpri Manufacturer Invalid Alarm" },
    { bit: 6, name: "Cpri Rate Mismatch Alarm" },
  ];

  const dxru10gAlarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "Overall Alarm" },
    { bit: 1, name: "Hardware Fault Alarm" },
    { bit: 2, name: "Over Temperature Alarm" },
    { bit: 3, name: "Firmware Mismatch Alarm" },
    { bit: 4, name: "DC Voltage Lower Alarm" },
    { bit: 5, name: "Overflow Alarm" },
    { bit: 6, name: "Low Transmission Alarm" },
  ];

  const formatDxruSwitch = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    return String(value) === "1" ? "On" : String(value) === "0" ? "Off" : String(value);
  };

  const formatDxruDbm = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return `${num.toFixed(2)} dBm`;
  };

  const formatDxruSfpDbm = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return num.toFixed(2);
  };

  const formatDxru10gSfpMetric = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return String(num);
  };

  const renderDxru10gMetricValueWithUnit = (
    value: unknown,
    unit: string,
    formatter: (v: unknown) => string = formatDxru10gSfpMetric
  ) => {
    const formattedValue = formatter(value);

    if (formattedValue === "N/A") {
      return <span className="font-medium text-muted-foreground">N/A</span>;
    }

    return <span className="font-medium">{formattedValue} {unit}</span>;
  };

  const renderStatusAlarmCard = (
    title: string,
    items: any[],
    numberKey: string,
    alarmKey: string,
    maskKey: string,
    labelPrefix: string
  ) => {
    if (items.length === 0) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item: any, index: number) => {
              const itemNumber = item?.[numberKey] ?? index + 1;
              const isAlarmActive = Number(item?.[alarmKey]) > 0;
              const isMaskEnabled = Number(item?.[maskKey]) > 0;

              return (
                <div
                  key={`${title}-${itemNumber}-${index}`}
                  className="rounded-xl border p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {labelPrefix} {itemNumber}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                      }}
                    />
                  </div>

                  <div className="mt-3 text-xs flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </span>
                    <Checkbox
                      checked={isMaskEnabled}
                      disabled
                      size="small"
                      sx={{
                        padding: 0,
                        transform: "scale(0.7)",
                        color: "var(--foreground)",
                        "&.Mui-disabled": {
                          color: "var(--muted-foreground)",
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDxru10gSfpStatusCard = () => {
    if (!hasDxru10gSfpInfo) return null;

    return (
      <Card className="shadow-sm border mb-2">
        <CardHeader className="px-4 py-3 border-b">
          <CardTitle className="text-sm font-semibold tracking-tight">
            SFP Status
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {dxru10gSfpColumns.map((idx) => {
              const alarmsVal = getDxru10gSfpValue("SFPAlarms", idx);
              const maskVal = getDxru10gSfpValue("SFPAlarmsMask", idx);

              return (
                <div
                  key={`dxru10g-sfp-status-${idx}`}
                  className="rounded-xl border bg-card p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold">
                      Port {idx}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(
                          isDisconnected,
                          Number(alarmsVal) > 0
                        ),
                      }}
                    />
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Temperature
                      </span>

                      {renderDxru10gMetricValueWithUnit(
                        getDxru10gSfpValue("SFPTemperature", idx),
                        "°C"
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        TX Power
                      </span>

                      {renderDxru10gMetricValueWithUnit(
                        getDxru10gSfpValue("SFPTxPower", idx),
                        "dBm",
                        formatDxruSfpDbm
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        RX Power
                      </span>

                      {renderDxru10gMetricValueWithUnit(
                        getDxru10gSfpValue("SFPRxPower", idx),
                        "dBm",
                        formatDxruSfpDbm
                      )}
                    </div>
                  </div>

                  <div className="mt-4 border-t pt-3 space-y-2">
                    {dxru10gSfpAlarmDefinitions.map((alarmDef) => {
                      const isAlarmActive = isBitSet(alarmsVal, alarmDef.bit);
                      const isMaskEnabled = isBitSet(maskVal, alarmDef.bit);

                      return (
                        <div
                          key={`dxru10g-sfp-status-${idx}-${alarmDef.bit}`}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: getAlarmLedColor(
                                  isDisconnected,
                                  isAlarmActive
                                ),
                              }}
                            />

                            <span className="text-[11px] truncate text-muted-foreground">
                              {alarmDef.name}
                            </span>
                          </div>

                          <Checkbox
                            checked={isMaskEnabled}
                            disabled
                            size="small"
                            sx={{
                              padding: 0,
                              transform: "scale(0.7)",
                              color: "var(--foreground)",
                              "&.Mui-disabled": {
                                color: "var(--muted-foreground)",
                              },
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (alarmsOnly) {
    if (!hasDxru10gSfpInfo && !hasDxru10gAlarms) return null;

    return (
      <>
        {renderDxru10gSfpStatusCard()}

        {hasDxru10gAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">DxRU10G Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={dxru10gAlarms}
                alarmsMask={dxru10gAlarmsMask}
                alarmDefinitions={dxru10gAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  if (
    !hasDeviceInfo &&
    !hasDxru10gAlarms &&
    !hasDxru10gRfInfo &&
    !hasDxru10gSfpInfo &&
    !hasPowerDetectorChart &&
    !hasFanAlarm &&
    !hasVswrAlarm &&
    !hasAntennaAlarm &&
    !hasPaAlarm
  ) return null;

  return (
    <>
      {hasDeviceInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {dxru10gConnectDeuPortNum && <DetailRow label="Connected DEU Port" value={dxru10gConnectDeuPortNum} />}
            {dxru10gSiteInfo && <DetailRow label="Site" value={dxru10gSiteInfo} />}
            {dxru10gEquipmentModel && <DetailRow label="Model" value={dxru10gEquipmentModel} />}
            {dxru10gEquipmentSn && <DetailRow label="Serial Number" value={dxru10gEquipmentSn} />}
            {dxru10gTemperature !== undefined && <DetailRow label="Temperature" value={`${dxru10gTemperature}°C`} />}
            {dxru10gFirmwareVersion && <DetailRow label="Firmware Version" value={dxru10gFirmwareVersion} />}
            {dxru10gHardwareVersion && <DetailRow label="Hardware Version" value={dxru10gHardwareVersion} />}
          </CardContent>
        </Card>
      )}

      {hasDxru10gRfInfo && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Type</TableHead>
                  {dxru10gSwitchColumns.map((idx) => (
                    <TableHead key={`hdr-${idx}`} className="text-left min-w-[110px]">
                      Port {idx}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Switch</TableCell>
                  {dxru10gSwitchColumns.map((idx) => (
                    <TableCell key={`switch-${idx}`} className="text-left">
                      {formatDxruSwitch(getDxru10gPortValue("RFSwitch", idx))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>Downlink Power</TableCell>
                  {dxru10gSwitchColumns.map((idx) => (
                    <TableCell key={`dl-${idx}`} className="text-left">
                      {formatDxruDbm(getDxru10gPortValue("DlPwrOut", idx))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>Uplink Power</TableCell>
                  {dxru10gSwitchColumns.map((idx) => (
                    <TableCell key={`ul-${idx}`} className="text-left">
                      {formatDxruDbm(getDxru10gPortValue("UlPwrIn", idx))}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dlPowerDetectorChartConfig && dlPowerDetectorAdditionalParams && (
        <Card className="mb-2">
          <CardContent className="px-4 pb-4 pt-0">
            <div style={{ height: 300, width: "100%" }}>
              <DasPowerDetectorChart
                config={{
                  ...dlPowerDetectorChartConfig,
                  title: "DL Power Detector",
                  csvFilename: "dxru10g_dl_power_detector",
                  chartImageFilename: "dxru10g_dl_power_detector_chart",
                }}
                onFullscreenToggle={() => setFullscreenChartType("dl")}
                isFullscreen={false}
                additionalParams={dlPowerDetectorAdditionalParams}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {ulPowerDetectorChartConfig && ulPowerDetectorAdditionalParams && (
        <Card className="mb-2">
          <CardContent className="px-4 pb-4 pt-0">
            <div style={{ height: 300, width: "100%" }}>
              <DasPowerDetectorChart
                config={{
                  ...ulPowerDetectorChartConfig,
                  title: "UL Power Detector",
                  csvFilename: "dxru10g_ul_power_detector",
                  chartImageFilename: "dxru10g_ul_power_detector_chart",
                }}
                onFullscreenToggle={() => setFullscreenChartType("ul")}
                isFullscreen={false}
                additionalParams={ulPowerDetectorAdditionalParams}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {renderDxru10gSfpStatusCard()}

      {hasDxru10gAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">DxRU10G Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={dxru10gAlarms}
              alarmsMask={dxru10gAlarmsMask}
              alarmDefinitions={dxru10gAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {renderStatusAlarmCard(
        "Fan Alarm",
        dxru10gFanAlarmSorted,
        "dxRu10GFanNumber",
        "dxRu10GFanAlarm",
        "dxRu10GFanAlarmMask",
        "Fan"
      )}

      {renderStatusAlarmCard(
        "VSWR Alarm",
        dxru10gVswrAlarmSorted,
        "dxRu10GVswrNumber",
        "dxRu10GVswrAlarm",
        "dxRu10GVswrAlarmMask",
        "VSWR"
      )}

      {renderStatusAlarmCard(
        "Antenna Alarm",
        dxru10gAntennaAlarmSorted,
        "dxRu10GAntennaNumber",
        "dxRu10GAntennaAlarm",
        "dxRu10GAntennaAlarmMask",
        "Antenna"
      )}

      {renderStatusAlarmCard(
        "PA Alarm",
        dxru10gPaAlarmSorted,
        "dxRu10GPaNumber",
        "dxRu10GPaAlarm",
        "dxRu10GPaAlarmMask",
        "PA"
      )}

      <Dialog
        open={fullscreenChartType !== null}
        onClose={() => setFullscreenChartType(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            minHeight: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid var(--border)",
            pb: 2,
            px: 4,
          }}
        >
          <IconButton onClick={() => setFullscreenChartType(null)} size="small" sx={{ color: "var(--foreground)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChartType === "dl" && dlPowerDetectorChartConfig && dlPowerDetectorAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...dlPowerDetectorChartConfig,
                title: "",
                csvFilename: "dxru10g_dl_power_detector",
                chartImageFilename: "dxru10g_dl_power_detector_chart",
              }}
              onFullscreenToggle={() => setFullscreenChartType(null)}
              isFullscreen={true}
              additionalParams={dlPowerDetectorAdditionalParams}
            />
          )}

          {fullscreenChartType === "ul" && ulPowerDetectorChartConfig && ulPowerDetectorAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...ulPowerDetectorChartConfig,
                title: "",
                csvFilename: "dxru10g_ul_power_detector",
                chartImageFilename: "dxru10g_ul_power_detector_chart",
              }}
              onFullscreenToggle={() => setFullscreenChartType(null)}
              isFullscreen={true}
              additionalParams={ulPowerDetectorAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dxru10gDeviceDetails;
